import type { SymbolBacking } from '../symbols/backing';
import type { AnnotationFontFamilyId, AnnotationFontWeight } from './typography';
import type { SymbolGraphic } from '../symbols/model';

export type AnnotationLayerId = string;
export type AnnotationId = string;
export type AnnotationCoordinate = readonly [number, number];
export type AnnotationDash = 'dashed' | 'solid';
export type AnnotationEndpointStyle = 'none' | 'arrow' | 'filled-circle' | 'filled-square';

export interface AnnotationBase {
  readonly id: AnnotationId;
  readonly name: string;
  readonly visible: boolean;
  readonly includeInLegend?: boolean;
  readonly legendLabel?: string;
}

export interface MarkerAnnotation extends AnnotationBase {
  readonly heightAboveMapM?: number;
  readonly type: 'marker';
  readonly position: AnnotationCoordinate;
  readonly style: {
    readonly shape: SymbolGraphic<'circle' | 'diamond' | 'square'>;
    readonly tint?: string | null;
    readonly backing?: SymbolBacking;
    readonly rotationDegrees?: number;
    readonly anchor?: 'center' | 'bottom';
    readonly fillColour: string;
    readonly outlineColour: string;
    readonly size: number;
    readonly opacity: number;
  };
}

export interface LineAnnotation extends AnnotationBase {
  readonly heightAboveMapM?: number;
  readonly type: 'line';
  readonly coordinates: readonly AnnotationCoordinate[];
  readonly style: {
    readonly colour: string;
    readonly width: number;
    readonly opacity: number;
    readonly dash: AnnotationDash;
    readonly endStyle: AnnotationEndpointStyle;
  };
}

export interface AreaAnnotationStyle {
  readonly fillColour: string;
  readonly fillOpacity: number;
  readonly outlineColour: string;
  readonly outlineWidth: number;
  readonly outlineDash: AnnotationDash;
}

/** First part stays in coordinates/holes for compatibility with existing projects. */
export interface AnnotationAreaPart {
  readonly coordinates: readonly AnnotationCoordinate[];
  readonly holes?: readonly (readonly AnnotationCoordinate[])[];
}

export interface PolygonAnnotation extends AnnotationBase {
  readonly type: 'polygon';
  readonly coordinates: readonly AnnotationCoordinate[];
  readonly holes?: readonly (readonly AnnotationCoordinate[])[];
  readonly additionalParts?: readonly AnnotationAreaPart[];
  readonly style: AreaAnnotationStyle;
}

export interface RectangleAnnotation extends AnnotationBase {
  readonly type: 'rectangle';
  readonly coordinates: readonly AnnotationCoordinate[];
  readonly rotationDegrees: number;
  readonly style: AreaAnnotationStyle & {
    /** Visual corner radius in logical display pixels. */
    readonly cornerRadius: number;
  };
}

export interface EllipseAnnotation extends AnnotationBase {
  readonly type: 'ellipse';
  /** Opposite corners of the geographic Web-Mercator-aligned bounding extent. */
  readonly bounds: readonly [AnnotationCoordinate, AnnotationCoordinate];
  readonly circleLocked: boolean;
  readonly rotationDegrees: number;
  readonly style: AreaAnnotationStyle;
}

export interface AnnotationTextStyle {
  readonly fontFamily: AnnotationFontFamilyId;
  readonly fontWeight?: AnnotationFontWeight;
  readonly fontStyle?: 'normal' | 'italic';
  readonly textHaloWidth?: number;
  readonly textHaloColour?: string;
  /** Absent means automatic. Manual content height expands to the safe measured minimum. */
  readonly boxHeight?: number;
  readonly fontSize: number;
  readonly colour: string;
  readonly alignment: 'left' | 'centre' | 'right';
  readonly backgroundColour: string;
  readonly backgroundOpacity: number;
  readonly borderColour: string;
  readonly borderWidth: number;
  readonly padding: number;
  /** Maximum authored text-box width in logical display pixels. */
  readonly boxWidth: number;
  /** Visual corner radius in logical display pixels. */
  readonly cornerRadius: number;
}

export interface TextAnnotation extends AnnotationBase {
  readonly heightAboveMapM?: number;
  readonly type: 'text';
  readonly position: AnnotationCoordinate;
  readonly text: string;
  readonly style: AnnotationTextStyle;
}

export interface CalloutAnnotation extends AnnotationBase {
  readonly labelHeightAboveMapM?: number;
  readonly keepLeaderVerticalOnScreen?: boolean;
  readonly type: 'callout';
  readonly target: AnnotationCoordinate;
  readonly textPosition: AnnotationCoordinate;
  readonly text: string;
  readonly style: AnnotationTextStyle & {
    readonly leaderColour: string;
    readonly leaderStyle?: 'tapered' | 'straight';
    readonly leaderOpacity?: number;
    readonly leaderWidth: number;
    readonly leaderDash: AnnotationDash;
    readonly targetStyle: AnnotationEndpointStyle;
  };
}

export interface FocusAreaAnnotation extends AnnotationBase {
  readonly type: 'focus-area';
  readonly coordinates: readonly AnnotationCoordinate[];
  readonly holes?: readonly (readonly AnnotationCoordinate[])[];
  readonly additionalParts?: readonly AnnotationAreaPart[];
  readonly style: {
    readonly fadeColour: string;
    readonly fadeOpacity: number;
    readonly boundaryVisible: boolean;
    readonly boundaryColour: string;
    readonly boundaryWidth: number;
    readonly boundaryDash: AnnotationDash;
    readonly fadeDirection: 'inside' | 'outside';
    readonly scope: 'basemap' | 'basemap-and-data';
  };
}

export type Annotation =
  | MarkerAnnotation
  | LineAnnotation
  | PolygonAnnotation
  | RectangleAnnotation
  | EllipseAnnotation
  | TextAnnotation
  | CalloutAnnotation
  | FocusAreaAnnotation;

export type AnnotationType = Annotation['type'];

export interface AnnotationLayer {
  readonly id: AnnotationLayerId;
  readonly name: string;
  readonly visible: boolean;
  readonly includeInLegend?: boolean;
  readonly legendLabel?: string;
  /** Deterministic object order, oldest first and topmost last. */
  readonly annotations: readonly Annotation[];
}

export const ANNOTATION_LIMITS = {
  heightAboveMapM: { minimum: 0, maximum: 100_000 },
  layersPerProject: 64,
  annotationsPerLayer: 1_000,
  annotationsPerProject: 5_000,
  verticesPerAnnotation: 10_000,
  nameLength: 120,
  textLength: 4_000,
  width: { minimum: 0.25, maximum: 32 },
  markerSize: { minimum: 4, maximum: 64 },
  fontSize: { minimum: 8, maximum: 96 },
  padding: { minimum: 0, maximum: 32 },
  textBoxWidth: { minimum: 40, maximum: 800 },
  textBoxHeight: { minimum: 8, maximum: 1200 },
  textHaloWidth: { minimum: 0, maximum: 3 },
  cornerRadius: { minimum: 0, maximum: 128 },
} as const;

export type AnnotationCreationTool = AnnotationType;

export type AnnotationMovePart =
  | 'object'
  | 'target'
  | 'text'
  | 'text-width'
  | 'text-height'
  | 'text-size'
  | `vertex-${number}`
  | `rectangle-corner-${0 | 1 | 2 | 3}`
  | `ellipse-handle-${'left' | 'right' | 'top' | 'bottom'}`
  | `midpoint-${number}`;
